/**
 * "Aprovar" and "Responder" on an agent that stopped to ask (docs/refactor/51, §6.4) — in the inbox, and from the
 * phone's notification, whose buttons open the app here with ?item=…&do=approve|reply. The answer goes to the same
 * project, computer and agent, with the request and the agent's last answer, so it knows what it is about.
 */

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, MessageSquareReply, Send } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'

export function InboxAnswer({ itemKey, start }: { itemKey: string; start: 'approve' | 'reply' | null }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [replying, setReplying] = useState(start === 'reply')
  const [text, setText] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)

  const answer = useMutation({
    mutationFn: (body: { approve: true } | { approve: false; text: string }) => api.inbox.answer(itemKey, body),
    onSuccess: (_, body) => {
      setSent(body.approve ? t('inbox.answer.approved') : t('inbox.answer.sent'))
      setReplying(false)
      setText('')
      void queryClient.invalidateQueries({ queryKey: ['inbox'] })
      void queryClient.invalidateQueries({ queryKey: ['prompt-queue'] })
    },
  })

  // From the notification's "Aprovar": once, as the tap meant — never again on a reload of the same address.
  const approvedOnce = useRef(false)
  useEffect(() => {
    if (start !== 'approve' || approvedOnce.current) return
    approvedOnce.current = true
    const mark = `devpulse:answered:${itemKey}`
    try {
      if (sessionStorage.getItem(mark)) return
      sessionStorage.setItem(mark, '1')
    } catch {
      // Storage blocked: the ref above still keeps it to once.
    }
    answer.mutate({ approve: true })
  }, [start, itemKey, answer])

  useEffect(() => {
    if (replying) box.current?.focus()
  }, [replying])

  const problem = answer.error instanceof ApiError && answer.error.code && ['spend_limit', 'still_running', 'not_found', 'remote_off'].includes(answer.error.code)
    ? t(`inbox.answer.error.${answer.error.code}`)
    : answer.error ? t('error.generic') : null

  if (sent) return <p className="tiny inbox__answered" role="status"><Check size={12} aria-hidden /> {sent}</p>

  return (
    <div className="inbox__answer stack stack--tight">
      <div className="row row--tight">
        <button type="button" className="btn btn--sm btn--primary" onClick={() => answer.mutate({ approve: true })} disabled={answer.isPending}>
          {answer.isPending ? <Loader2 size={12} className="spin" /> : <Check size={12} />} {t('inbox.answer.approve')}
        </button>
        <button type="button" className="btn btn--sm" onClick={() => setReplying(!replying)} aria-expanded={replying}>
          <MessageSquareReply size={12} /> {t('inbox.answer.reply')}
        </button>
      </div>
      {replying && (
        <form
          className="row row--tight"
          onSubmit={(event) => {
            event.preventDefault()
            if (text.trim()) answer.mutate({ approve: false, text: text.trim() })
          }}
        >
          <textarea ref={box} className="input inbox__reply" rows={2} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('inbox.answer.placeholder')} aria-label={t('inbox.answer.reply')} />
          <button type="submit" className="btn btn--sm btn--primary btn--icon" disabled={!text.trim() || answer.isPending} aria-label={t('inbox.answer.send')} title={t('inbox.answer.send')}>
            <Send size={13} />
          </button>
        </form>
      )}
      {problem && <p className="tiny" role="alert" style={{ margin: 0 }}>{problem}</p>}
    </div>
  )
}
